// Iter 306 — "Have a question" / "Request review" on a pro forma.
import { useState } from "react";
import { HelpCircle, Loader2, Flag } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export function InquiryDialog({ partnerKey, period, kind, onClose, onCreated }) {
  const [message, setMessage] = useState("");
  const [domains, setDomains] = useState("");
  const [busy, setBusy] = useState(false);
  const review = kind === "review";
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(
        `/commissions/partners/${encodeURIComponent(partnerKey)}/periods/${encodeURIComponent(period)}/inquiries`,
        { kind, message, domains: domains.split(",").map((s) => s.trim()).filter(Boolean) });
      toast.success(review ? "Review requested — the team has been notified" : "Question sent — the team has been notified");
      onCreated?.(data);
      onClose();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not send"); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="inquiry-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {review ? <Flag className="h-4 w-4 text-amber-600" /> : <HelpCircle className="h-4 w-4 text-primary" />}
            {review ? `Request a review — ${period}` : `Have a question — ${period}`}
          </DialogTitle>
          <DialogDescription>
            {review
              ? "Tell us what looks wrong. The pro forma stays open until the review is resolved and, if needed, a corrected version is issued."
              : "Ask anything about this month's pro forma. Your Partner Success manager and the commissions team are notified."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">{review ? "What should be reviewed?" : "Your question"}</Label>
            <Textarea rows={5} value={message} onChange={(e) => setMessage(e.target.value)} autoFocus
                      placeholder={review ? "e.g. Kingston Midas renewal is missing and the rate on acme.ca looks low" : "e.g. Why is the October total lower than September?"}
                      data-testid="inquiry-message" />
          </div>
          <div>
            <Label className="text-xs">Deals concerned (optional, comma-separated domains)</Label>
            <Input value={domains} onChange={(e) => setDomains(e.target.value)} placeholder="acme.ca, kingstonmidas.com" data-testid="inquiry-domains" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="inquiry-cancel">Cancel</Button>
          <Button onClick={submit} disabled={busy || message.trim().length < 3}
                  className={review ? "bg-amber-600 text-white hover:bg-amber-700" : ""} data-testid="inquiry-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : review ? "Request review" : "Send question"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
